import { NavListCreateNewButton, Select, SelectProps, useModal } from '@billy/design-system'
import React, { ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ModalId } from '../../../../enums/modalId'
import { useZervantSettings } from '../../../invoicingSettings/hooks/useZervantSettings'
import { ProductUnitCategory } from '../../enums/productUnitCategory'
import { useRecentProductUnits } from '../../hooks/useRecentProductUnits'
import { getConfiguredProductUnits } from '../../utils/getConfiguredProductUnits'
import { getConfiguredUnitByValue } from '../../utils/getConfiguredUnitByValue'
import { getProductUnitName } from '../../utils/getProductUnitName'
import { ProductUnitsModal } from '../ProductUnitsModal'
import { useSaveProductUnits } from './hooks/useSaveProductUnits'
import { getProductUnitItems } from './utils/getProductUnitItems'

interface ProductUnitSelectProps extends Omit<SelectProps, 'items' | 'onChange' | 'onSelect' | 'selectedId'> {
  onSelect: (unit: string | undefined) => void
  value: string | undefined
}

type HandleSelect = (id: string | undefined, value: string | undefined) => void

export const ProductUnitSelect = ({ onSelect, value, ...props }: ProductUnitSelectProps): ReactElement => {
  const [isSelectOpen, setIsSelectState] = useState(false)

  const { t } = useTranslation()
  const { close, open } = useModal(ModalId.ProductUnitModal)

  const { isLoading, data: zervantSettings } = useZervantSettings()

  const rawUnits = zervantSettings?.managedFields?.UNIT_OPTIONS
  const configuredUnits = useMemo(() => getConfiguredProductUnits(rawUnits, t), [rawUnits, t])

  const matchedUnit = useMemo(
    () => (value && rawUnits ? getConfiguredUnitByValue(rawUnits, value) : undefined),
    [rawUnits, value],
  )
  const displayValue = matchedUnit ? getProductUnitName(matchedUnit, t) : value
  const isValueConfigured = !!matchedUnit
  const { addRecentUnit, recentUnits, refreshRecentUnits, removeRecentUnit } = useRecentProductUnits(configuredUnits)
  const { saveProductUnits } = useSaveProductUnits({
    isValueConfigured,
    onClose: close,
    onSelect,
    onUnitAdded: addRecentUnit,
    value: displayValue,
  })

  const unitItems = useMemo(() => {
    if (isLoading) {
      return []
    }

    // Select resolves its label from the items, so a saved unit that is no longer
    // configured has to be kept in the list or the field renders empty
    const availableUnits =
      value && !isValueConfigured
        ? [...configuredUnits, { category: ProductUnitCategory.Custom, name: value }]
        : configuredUnits

    return getProductUnitItems({
      onRemoveRecentUnit: removeRecentUnit,
      recentUnits,
      t,
      units: availableUnits,
    })
  }, [configuredUnits, isLoading, isValueConfigured, recentUnits, removeRecentUnit, t, value])

  const handleSelect = useCallback<HandleSelect>(
    (_, value) => {
      onSelect(value)

      if (value) {
        addRecentUnit(value)
      }
    },
    [addRecentUnit, onSelect],
  )

  const handleDropdownOpen = useCallback(() => {
    if (!isSelectOpen) {
      refreshRecentUnits()
    }
    setIsSelectState(true)
  }, [isSelectOpen, refreshRecentUnits])

  const handleDropdownClose = useCallback(() => {
    setIsSelectState(false)
  }, [])

  const handleCreateNewProductUnit = useCallback(() => {
    setIsSelectState(false)
    open()
  }, [open])

  return (
    <>
      <Select
        {...props}
        dropdownFetching={isLoading}
        dropdownFooter={
          <NavListCreateNewButton onClick={handleCreateNewProductUnit}>
            {t('products.units.create_custom_unit')}
          </NavListCreateNewButton>
        }
        dropdownSize="xl"
        isOpen={isSelectOpen}
        items={unitItems}
        onDropdownClose={handleDropdownClose}
        onDropdownOpen={handleDropdownOpen}
        onSelect={handleSelect}
        placement="bottom-start"
        selectedId={displayValue}
        withSearch
      />

      {/* Raw units, not `configuredUnits`: the modal saves this list back to settings as-is, and a
      standard unit's `name` must stay the backend-stable value, not the current UI language's translation */}
      <ProductUnitsModal
        id={ModalId.ProductUnitModal}
        onClose={close}
        onSave={saveProductUnits}
        units={rawUnits ?? []}
      />
    </>
  )
}
